import { useState, useEffect } from 'react';
import { useComandosDoJogo } from '@/hooks/useComandosDoJogo';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import {
  User,
  Package,
  CreditCard,
  Settings,
  Copy,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Ticket,
  Shield,
  Mail,
  Calendar,
  Edit,
  Camera,
  Loader2,
  XCircle,
  AlertCircle,
  Truck,
  PackageCheck,
  Gift,
  Sparkles,
  Award,
  Eye,
  ChevronRight,
  Handshake,
  Trash2,
  Link2,
  Download as DownloadIcon,
  Crown,
  Gamepad2,
} from 'lucide-react';
import { MinhasCompras } from '@/components/digital/MinhasCompras';
import { MeusVips } from '@/components/vip/MeusVips';
import { ehLojaDigital } from '@/lib/loja-digital';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useMyPartner } from '@/hooks/useMyPartner';
import { useTheme } from '@/contexts/ThemeContext';
import { tickets } from '@/mocks/storeConfig';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { toast } from 'sonner';
import { playerAuthService } from '@/lib/services/player-auth.service';
import { useStore } from '@/contexts/StoreContext';
import { orderService, PlayerOrder } from '@/lib/services/order.service';
import { OrderDetailModal } from '@/components/modals/OrderDetailModal';
import { creditService, CreditTransaction } from '@/lib/services/credit.service';
import forumStoreService, { ForumBadge } from '@/lib/services/forumStore.service';
import AnimatedBadge, { BadgeStyle, BadgeAnimation } from '@/components/common/AnimatedBadge';
import { useAvatar } from '@/hooks/useAvatar';
import { AddCreditsDialog } from '@/components/credits/AddCreditsDialog';
import { useTextosDaLoja } from '@/construtor/textos';
import { addonAtivo } from '@/lib/addons';
import { discordService, type MyDiscordLink } from '@/lib/services/discord.service';
import { DiscordLinkCard } from '@/components/discord/DiscordLinkCard';
import { GameLinkCard } from '@/components/conta/GameLinkCard';

import { Caixa } from '@/construtor/caixa';
export default function ProfilePage() {
  const { t, i18n } = useTextosDaLoja();
  const comandos = useComandosDoJogo();
  const { user, isAuthenticated, updateUserEmail } = useAuth();
  const { isPartner } = useMyPartner();
  const { getAvatarUrl } = useAvatar();
  const { config } = useTheme();
  const { store } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { copy } = useCopyToClipboard();

  const activeTab = searchParams.get('tab') || 'account';
  // Loja de produtos digitais: a entrega fica em "Minhas compras", e o nick é gerado (mostra o nome).
  const lojaDigital = ehLojaDigital(store);
  const nomeVisivel = lojaDigital ? user?.name || user?.username : user?.username || user?.name;

  // Vínculo do Discord: a aba só existe com o módulo ligado e o bot da loja no servidor.
  const [discordLink, setDiscordLink] = useState<MyDiscordLink | null>(null);
  const discordAtivo = addonAtivo(store?.addons, 'discord');
  // VIP do jogo: só em loja de jogo com o módulo VIP ligado.
  const mostrarVips = addonAtivo(store?.addons, 'vip') && !lojaDigital;
  useEffect(() => {
    if (!store?.id || !isAuthenticated || !discordAtivo) return;
    discordService
      .getMine(store.id)
      .then(setDiscordLink)
      .catch(() => setDiscordLink(null));
  }, [store?.id, isAuthenticated, discordAtivo]);
  const mostrarDiscord = !!discordLink && (discordLink.available || !!discordLink.link);

  // Real orders from API
  const [playerOrders, setPlayerOrders] = useState<PlayerOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Real credit transactions from API
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Forum badges
  const [forumBadges, setForumBadges] = useState<ForumBadge[]>([]);
  const [loadingBadges, setLoadingBadges] = useState(false);

  // Fetch player orders
  useEffect(() => {
    if (store && isAuthenticated) {
      setLoadingOrders(true);
      orderService.listPlayerOrders(store.id)
        .then((orders) => setPlayerOrders(orders))
        .catch(() => setPlayerOrders([]))
        .finally(() => setLoadingOrders(false));
    }
  }, [store, isAuthenticated]);

  // Fetch credit transactions
  useEffect(() => {
    if (store && isAuthenticated) {
      setLoadingTransactions(true);
      creditService.getCreditHistory(store.id)
        .then((transactions) => setCreditTransactions(transactions))
        .catch(() => setCreditTransactions([]))
        .finally(() => setLoadingTransactions(false));
    }
  }, [store, isAuthenticated]);

  // Fetch forum badges
  useEffect(() => {
    if (user?.id && isAuthenticated) {
      setLoadingBadges(true);
      forumStoreService.getPlayerBadges(user.id)
        .then((badges: ForumBadge[]) => setForumBadges(badges))
        .catch(() => setForumBadges([]))
        .finally(() => setLoadingBadges(false));
    }
  }, [user?.id, isAuthenticated]);

  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);

  const handleOpenOrderDetail = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOrderDetailOpen(true);
  };

  // Estado para formulários
  const [emailData, setEmailData] = useState({ newEmail: '', password: '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [apagarAberto, setApagarAberto] = useState(false);
  const [apagando, setApagando] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  /**
   * Apaga a conta do jogador.
   *
   * O que a API faz: tira o dado pessoal e deixa a conta inativa, sem remover
   * a linha (pedido e crédito são registro contábil). Conta com saldo é
   * recusada com `HAS_CREDITS`, e aí o aviso diz quanto falta gastar.
   */
  const handleDeleteAccount = async () => {
    if (!store) return;

    setApagando(true);
    try {
      await playerAuthService.deleteAccount(store.id);
      await playerAuthService.logout(store.id);
      toast.success(t('store.profile.deleted'));
      window.location.href = '/';
    } catch (err: any) {
      const dados = err?.response?.data;
      if (dados?.code === 'HAS_CREDITS') {
        toast.error(t('store.profile.delete_has_credits', { balance: dados.balance }));
      } else {
        toast.error(t('store.profile.delete_error'));
      }
    } finally {
      setApagando(false);
      setApagarAberto(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!store) {
      toast.error(t('store.profile.store_not_loaded'));
      return;
    }
    
    if (!emailData.newEmail || !emailData.password) {
      toast.error(t('store.profile.fill_all'));
      return;
    }

    if (emailData.newEmail === user?.email) {
      toast.error(t('store.profile.email_must_differ'));
      return;
    }

    setLoadingEmail(true);
    try {
      const result = await playerAuthService.changeEmail(store.id, emailData);
      toast.success(result.message || t('store.profile.email_changed'));
      
      // Atualizar o email no contexto
      if (updateUserEmail) {
        updateUserEmail(emailData.newEmail);
      }
      
      // Limpar campos
      setEmailData({ newEmail: '', password: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('store.profile.email_error'));
    } finally {
      setLoadingEmail(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!store) {
      toast.error(t('store.profile.store_not_loaded'));
      return;
    }
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('store.profile.fill_all'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('store.profile.password_mismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('store.profile.password_short'));
      return;
    }

    setLoadingPassword(true);
    try {
      const result = await playerAuthService.changePassword(store.id, passwordData);
      toast.success(result.message || t('store.profile.password_changed'));
      
      // Limpar campos
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('store.profile.password_error'));
    } finally {
      setLoadingPassword(false);
    }
  };

  const formatPrice = (price: number) => {
    // Backend sends product prices already in reais (not cents)
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);
  };

  const formatCredits = (credits: number) => {
    // Credits are already in reais (same as price formatting)
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(credits);
  };

  const formatOrderPrice = (price: number) => {
    // Order values come in CENTS, need to divide by 100
    const priceInReais = price / 100;
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(priceInReais);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language || 'pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusConfig = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PAID':
      case 'DELIVERED':
        return { label: t('store.profile.status_done'), icon: CheckCircle2, variant: 'default' as const, className: 'bg-success/10 text-success border-success/20' };
      case 'PENDING':
        return { label: t('store.profile.status_pending'), icon: Clock, variant: 'secondary' as const, className: 'bg-warning/10 text-warning border-warning/20' };
      case 'CONFIRMED':
      case 'PROCESSING':
        return { label: t('store.profile.status_processing'), icon: Loader2, variant: 'secondary' as const, className: 'bg-primary/10 text-primary border-primary/20' };
      case 'SHIPPED':
        return { label: t('store.profile.status_shipped'), icon: Truck, variant: 'secondary' as const, className: 'bg-accent/10 text-accent border-accent/20' };
      case 'CANCELLED':
        return { label: t('store.profile.status_cancelled'), icon: XCircle, variant: 'destructive' as const, className: 'bg-destructive/10 text-destructive border-destructive/20' };
      case 'EXPIRED':
        return { label: t('store.order_detail.status_expired'), icon: Clock, variant: 'secondary' as const, className: 'bg-muted text-muted-foreground border-border' };
      case 'REFUNDED':
        return { label: t('store.order_detail.status_refunded'), icon: XCircle, variant: 'secondary' as const, className: 'bg-muted text-muted-foreground border-border' };
      default:
        return { label: status, icon: AlertCircle, variant: 'secondary' as const, className: 'bg-muted text-muted-foreground' };
    }
  };

  const getPaymentMethodLabel = (method?: string) => {
    if (!method) return '';
    const map: Record<string, string> = {
      PIX: 'PIX',
      CREDIT_CARD: t('store.profile.method_CREDIT_CARD'),
      DEBIT_CARD: t('store.profile.method_DEBIT_CARD'),
      MERCADO_PAGO: 'Mercado Pago',
      PAYPAL: 'PayPal',
      CREDITS: t('store.profile.method_CREDITS'),
      COUPON: t('store.profile.method_COUPON'),
      FREE: t('store.profile.method_FREE'),
      BOLETO: t('store.profile.method_BOLETO'),
    };
    return map[method.toUpperCase()] ?? method;
  };

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const recentOrders = playerOrders.slice(0, 3);
  const recentTransactions = creditTransactions.slice(0, 5);

  const playerBanner = store?.heroBackgroundUrl || config?.background?.image || '';

  return (
    <div className="min-h-screen bg-background pb-12">
      {/* Hero Banner com Avatar */}
      <Caixa id="perfil.topo">
      <div className="relative h-80 -mt-24 pt-24 overflow-hidden">
        {/* Banner Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: playerBanner ? `url(${playerBanner})` : undefined,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background" />
        </div>

        {/* Profile Info Overlay */}
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 h-full flex items-end pb-8">
          <div className="flex items-end gap-6 w-full">
            {/* Avatar */}
            <Caixa id="perfil.topo.avatar">
            <div className="relative group">
              <div className="relative status-online">
                <Avatar className="h-32 w-32 border-4 border-background shadow-2xl">
                  <AvatarImage src={getAvatarUrl(user?.username, 256)} />
                  <AvatarFallback className="bg-primary/20 text-primary text-4xl">
                    {user?.username?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              <Button
                size="icon"
                variant="secondary"
                className="absolute bottom-0 right-0 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            </Caixa>

            {/* User Info */}
            <Caixa id="perfil.topo.info">
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-display font-bold text-3xl text-white drop-shadow-lg">
                  {nomeVisivel}
                </h1>
                <Badge className="bg-primary/90 backdrop-blur-sm text-primary-foreground border-0 gap-1">
                  <Shield className="h-3 w-3" />
                  {user?.role}
                </Badge>
                {discordLink?.link && (
                  <Badge
                    className="bg-[#5865F2] hover:bg-[#5865F2] text-white border-0 gap-1 cursor-pointer"
                    onClick={() => setActiveTab('discord')}
                    data-testid="profile-discord-badge"
                  >
                    <Link2 className="h-3 w-3" />
                    {t('store.discord.badge', { username: discordLink.link.username })}
                  </Badge>
                )}
              </div>
              <p className="text-white/90 drop-shadow text-sm">
                {t('store.profile.member_since', { date: user?.createdAt ? formatDate(user.createdAt) : 'N/A' })}
              </p>
            </div>
            </Caixa>
          </div>
        </div>
      </div>
      </Caixa>

      {/* Tabs Navigation */}
      <Caixa id="perfil.abas">
      <div className="border-b border-border/30 bg-card/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent h-auto p-0 flex items-center gap-2 py-2">
              {[
                { id: 'account', label: t('store.profile.tab_account'), icon: User },
                ...(lojaDigital ? [{ id: 'library', label: t('store.digital.library.tab'), icon: DownloadIcon }] : []),
                ...(mostrarVips ? [{ id: 'vips', label: t('store.vip.tab'), icon: Crown }] : []),
                { id: 'orders', label: t('store.profile.tab_orders'), icon: Package },
                { id: 'credits', label: t('store.profile.tab_credits'), icon: CreditCard },
                { id: 'settings', label: t('store.profile.tab_settings'), icon: Settings },
                ...(!lojaDigital ? [{ id: 'game', label: t('store.game_link.tab'), icon: Gamepad2 }] : []),
                ...(mostrarDiscord ? [{ id: 'discord', label: t('store.discord.tab'), icon: Link2 }] : []),
              ].map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="group relative bg-transparent data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted/50 rounded-xl px-6 py-3 transition-all font-medium text-sm data-[state=inactive]:text-muted-foreground"
                >
                  <div className="flex items-center gap-2">
                    <tab.icon className="w-4 h-4 transition-transform group-hover:scale-110" />
                    {tab.label}
                  </div>
                  {/* Active indicator */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-primary transition-all data-[state=active]:w-3/4 opacity-0 data-[state=active]:opacity-100" />
                </TabsTrigger>
              ))}
              {isPartner && (
                <Link
                  to="/parceiro"
                  className="flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-muted-foreground transition-all hover:bg-muted/50 hover:text-foreground"
                >
                  <Handshake className="w-4 h-4" />
                  {t('store.header.partner_area')}
                </Link>
              )}
            </TabsList>
          </Tabs>
        </div>
      </div>
      </Caixa>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Account Tab - NEW */}
          <TabsContent value="account" className="mt-0 space-y-6">
            <Caixa id="perfil.conta">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Account Information Card */}
              <Caixa id="perfil.conta.informacoes" miolo>
              <Card className="lg:col-span-2 glass-strong border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <User className="w-6 h-6 text-primary" />
                    {t('store.profile.account_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.account_desc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Username */}
                  <Caixa id="perfil.conta.informacoes.usuario">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{lojaDigital ? t('store.auth.full_name') : t('store.profile.username')}</p>
                      <p className="font-semibold text-lg text-foreground">{nomeVisivel}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        copy(nomeVisivel || '');
                        toast.success(t('store.profile.name_copied'));
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  </Caixa>

                  {/* Email */}
                  <Caixa id="perfil.conta.informacoes.email">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{t('store.profile.email')}</p>
                      <p className="font-semibold text-foreground">{user?.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        copy(user?.email || '');
                        toast.success(t('store.profile.email_copied'));
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  </Caixa>

                  {/* Discord */}
                  {mostrarDiscord && discordLink && (
                    <Caixa id="perfil.conta.informacoes.discord">
                    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30" data-testid="profile-discord-row">
                      <Avatar className="w-12 h-12">
                        {discordLink.link?.avatarUrl && <AvatarImage src={discordLink.link.avatarUrl} alt="" />}
                        <AvatarFallback className="bg-[#5865F2]/15 text-[#5865F2]">
                          <Link2 className="w-6 h-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-muted-foreground">{t('store.discord.tab')}</p>
                        <p className="font-semibold text-foreground truncate">
                          {discordLink.link ? `@${discordLink.link.username}` : t('store.discord.not_linked')}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setActiveTab('discord')}>
                        {discordLink.link ? t('store.discord.manage') : t('store.discord.link_button')}
                      </Button>
                    </div>
                    </Caixa>
                  )}

                  {/* Role */}
                  <Caixa id="perfil.conta.informacoes.cargo">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{t('store.profile.role')}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="bg-primary/90 text-primary-foreground">
                          {user?.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  </Caixa>

                  {/* Member Since */}
                  <Caixa id="perfil.conta.informacoes.desde">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">{t('store.profile.member_since_label')}</p>
                      <p className="font-semibold text-foreground">
                        {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  </Caixa>
                </CardContent>
              </Card>
              </Caixa>

              {/* Wallet Card */}
              <Caixa id="perfil.conta.carteira" miolo>
              <Card className="glass-strong border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    {t('store.profile.wallet_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.wallet_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="font-display font-bold text-4xl text-primary mb-2">
                      {formatCredits(user?.credits || 0)}
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      {t('store.profile.credits_available')}
                    </p>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setCreditsDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('store.profile.add_credits')}
                    </Button>
                  </div>

                  <Separator className="my-4" />

                  {/* Recent Transactions Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('store.profile.recent_transactions')}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveTab('credits')}
                        className="text-xs"
                      >
                        {t('store.profile.view_all')}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {recentTransactions.slice(0, 3).map((transaction) => {
                        const isAdded = transaction.type === 'added';
                        const isRefunded = transaction.type === 'refunded';
                        return (
                          <Caixa id="perfil.conta.carteira.transacao" repetida key={transaction.id}>
                          <div
                            key={transaction.id}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="flex items-center gap-2">
                              {isAdded || isRefunded ? (
                                <TrendingUp className="w-3 h-3 text-success" />
                              ) : (
                                <TrendingDown className="w-3 h-3 text-destructive" />
                              )}
                              <span className="text-muted-foreground truncate max-w-[120px]">
                                {transaction.description}
                              </span>
                            </div>
                            <span
                              className={`font-medium ${
                                isAdded || isRefunded
                                  ? 'text-success'
                                  : 'text-foreground'
                              }`}
                            >
                              {isAdded || isRefunded ? '+' : ''}
                              {formatCredits(isAdded || isRefunded ? transaction.amount : -transaction.amount)}
                            </span>
                          </div>
                          </Caixa>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>

              {/* Gifts Card */}
              <Caixa id="perfil.conta.presentes" miolo>
              <Card className="glass-strong border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    {t('store.profile.gifts_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.gifts_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-6">
                      {t('store.profile.gifts_text')}
                    </p>
                    <Link to="/presentes">
                      <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Gift className="w-4 h-4 mr-2" />
                        {t('store.profile.gifts_button')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              </Caixa>

              {/* Apagar conta: exigência da Google Play, e o caminho para quem
                  desinstalou o app. A conta some para o jogador; a linha fica no
                  banco, inativa, porque pedido e crédito são registro contábil. */}
              <Caixa id="perfil.conta.apagar" miolo>
              <Card className="glass-strong border-destructive/40">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-destructive" />
                    {t('store.profile.delete_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.delete_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog open={apagarAberto} onOpenChange={setApagarAberto}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('store.profile.delete_button')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('store.profile.delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('store.profile.delete_confirm')}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={apagando}>{t('store.profile.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={(e) => {
                            e.preventDefault();
                            void handleDeleteAccount();
                          }}
                          disabled={apagando}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          {apagando ? t('store.profile.deleting') : t('store.profile.delete_button')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
              </Caixa>

              {/* Forum Badges Card */}
              <Caixa id="perfil.conta.insignias" miolo>
              <Card className="glass-strong border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    {t('store.profile.badges_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.badges_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingBadges ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : forumBadges.length === 0 ? (
                    <div className="text-center py-8">
                      <Award className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                      <p className="text-sm text-muted-foreground">
                        {t('store.profile.badges_empty')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('store.profile.badges_empty_hint')}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {forumBadges.map((badge) => (
                        <AnimatedBadge
                          key={badge.id}
                          name={badge.name}
                          icon={badge.icon}
                          style={(badge.style as BadgeStyle) || 'DEFAULT'}
                          animation={(badge.animation as BadgeAnimation) || 'NONE'}
                          className="text-xs"
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </Caixa>

              {/* Recent Orders Card */}
              <Caixa id="perfil.conta.pedidos" miolo>
              <Card className="lg:col-span-2 glass-strong border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Package className="w-5 h-5 text-primary" />
                      {t('store.profile.recent_orders_title')}
                    </CardTitle>
                    <CardDescription>{t('store.profile.recent_orders_desc')}</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('orders')}
                  >
                    {t('store.profile.view_all_orders')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingOrders ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : recentOrders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">{t('store.profile.no_orders')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recentOrders.map((order) => {
                        const statusConfig = getStatusConfig(order.status);
                        const StatusIcon = statusConfig.icon;
                        return (
                          <Caixa id="perfil.conta.pedidos.item" repetida key={order.id}>
                          <button
                            key={order.id}
                            onClick={() => handleOpenOrderDetail(order.id)}
                            className="text-left p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/50 hover:bg-muted/50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant={statusConfig.variant} className={`text-xs ${statusConfig.className}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(order.createdAt)}
                                </span>
                                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                              </div>
                            </div>
                            <p className="font-semibold text-sm text-muted-foreground mb-1">
                              {t('store.profile.order_number', { id: order.id.slice(0, 8).toUpperCase() })}
                            </p>
                            <p className="font-display font-bold text-2xl text-primary">
                              {formatOrderPrice(order.total)}
                            </p>
                          </button>
                          </Caixa>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              </Caixa>
            </div>
            </Caixa>
          </TabsContent>

          {/* Minhas compras (loja de produtos digitais) */}
          {lojaDigital && (
            <TabsContent value="library" className="mt-0">
              <Caixa id="perfil.biblioteca">
                <div className="space-y-4">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-foreground">{t('store.digital.library.title')}</h2>
                    <p className="text-sm text-muted-foreground">{t('store.digital.library.subtitle')}</p>
                  </div>
                  <MinhasCompras />
                </div>
              </Caixa>
            </TabsContent>
          )}

          {/* Meus VIPs (módulo VIP) */}
          {mostrarVips && (
            <TabsContent value="vips" className="mt-0">
              <Caixa id="perfil.vips">
                <div className="space-y-4">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-foreground">{t('store.vip.title')}</h2>
                    <p className="text-sm text-muted-foreground">{t('store.vip.subtitle', { vip: comandos.vip })}</p>
                  </div>
                  <MeusVips />
                </div>
              </Caixa>
            </TabsContent>
          )}

          {/* Orders Tab */}
          <TabsContent value="orders" className="mt-0">
            <Caixa id="perfil.pedidos" miolo>
            <Card className="glass-strong border-border/50">
              <CardHeader>
                <CardTitle className="text-2xl">{t('store.profile.orders_title')}</CardTitle>
                <CardDescription>{t('store.profile.orders_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">{t('store.profile.loading_orders')}</p>
                  </div>
                ) : playerOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium text-muted-foreground mb-2">
                      {t('store.profile.orders_empty_title')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('store.profile.orders_empty_desc')}
                    </p>
                    <Link to="/loja">
                      <Button className="mt-4">
                        {t('store.profile.go_to_store')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {playerOrders.map((order) => {
                      const statusConfig = getStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <Caixa id="perfil.pedidos.item" repetida key={order.id}>
                        <button
                          key={order.id}
                          data-testid="order-list-item"
                          onClick={() => handleOpenOrderDetail(order.id)}
                          className="w-full text-left flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/50 hover:bg-muted/50 transition-all hover:shadow-md gap-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <div className="flex items-start sm:items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Package className="w-6 h-6 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-foreground text-lg">
                                {t('store.profile.order_number', { id: order.id.slice(0, 8).toUpperCase() })}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(order.createdAt)}
                                {order.paymentMethod && ` • ${getPaymentMethodLabel(order.paymentMethod)}`}
                              </p>
                              {order.items.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {order.items.slice(0, 3).map((item, idx) => (
                                    <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                                      {item.quantity}x {item.productName}
                                    </span>
                                  ))}
                                  {order.items.length > 3 && (
                                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                                      {t('store.profile.more_items', { count: order.items.length - 3 })}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                            <p className="font-display font-bold text-2xl text-primary">
                              {formatOrderPrice(order.total)}
                            </p>
                            <div className="flex items-center gap-2">
                              <Badge variant={statusConfig.variant} className={`gap-1 ${statusConfig.className}`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                            </div>
                          </div>
                        </button>
                        </Caixa>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            </Caixa>
          </TabsContent>

          {/* Credits Tab */}
          <TabsContent value="credits" className="mt-0">
            <Caixa id="perfil.creditos">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Caixa id="perfil.creditos.saldo" miolo>
              <Card className="glass-strong border-border/50">
                <CardHeader>
                  <CardTitle className="text-xl">{t('store.profile.balance_title')}</CardTitle>
                  <CardDescription>{t('store.profile.balance_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="font-display font-bold text-5xl text-primary mb-2">
                      {formatCredits(user?.credits || 0)}
                    </p>
                    <p className="text-muted-foreground mb-6">
                      {t('store.profile.credits_available')}
                    </p>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setCreditsDialogOpen(true)}>
                      <Plus className="w-5 h-5 mr-2" />
                      {t('store.profile.add_credits')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </Caixa>

              <Caixa id="perfil.creditos.historico" miolo>
              <Card className="glass-strong border-border/50 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-xl">{t('store.profile.transactions_title')}</CardTitle>
                  <CardDescription>{t('store.profile.transactions_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingTransactions ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                      <p className="text-muted-foreground">{t('store.profile.loading_transactions')}</p>
                    </div>
                  ) : creditTransactions.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium text-muted-foreground">
                        {t('store.profile.transactions_empty')}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {creditTransactions.map((transaction) => {
                        const isAdded = transaction.type === 'added';
                        const isRefunded = transaction.type === 'refunded';
                        const isUsed = transaction.type === 'used';
                        
                        return (
                          <Caixa id="perfil.creditos.item" repetida key={transaction.id}>
                          <div
                            key={transaction.id}
                            className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/20 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                  isAdded || isRefunded
                                    ? 'bg-success/10'
                                    : 'bg-destructive/10'
                                }`}
                              >
                                {isAdded || isRefunded ? (
                                  <TrendingUp className="w-6 h-6 text-success" />
                                ) : (
                                  <TrendingDown className="w-6 h-6 text-destructive" />
                                )}
                              </div>
                              <div>
                                <p
                                  className={`font-semibold text-lg ${
                                    isAdded || isRefunded
                                      ? 'text-success'
                                      : 'text-foreground'
                                  }`}
                                >
                                  {isAdded || isRefunded ? '+' : ''}
                                  {formatCredits(isAdded || isRefunded ? transaction.amount : -transaction.amount)}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {transaction.description}
                                </p>
                              </div>
                            </div>
                            <span className="text-muted-foreground font-medium">
                              {formatDate(transaction.createdAt)}
                            </span>
                          </div>
                          </Caixa>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              </Caixa>
            </div>
            </Caixa>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-0">
            <div className="space-y-6">
              {/* Informações Gerais - Full Width (o nick de jogo; a loja digital não tem) */}
              {!lojaDigital && (
              <Caixa id="perfil.configuracoes.geral" miolo>
              <Card className="glass-strong border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <User className="w-6 h-6 text-primary" />
                    {t('store.profile.general_title')}
                  </CardTitle>
                  <CardDescription>{t('store.profile.general_desc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-base">{t('store.profile.username')}</Label>
                    <Input 
                      value={user?.username} 
                      className="h-11"
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('store.profile.username_hint')}
                    </p>
                  </div>
                </CardContent>
              </Card>
              </Caixa>
              )}

              {/* Alterar Email e Senha - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Alterar Email */}
                <Caixa id="perfil.configuracoes.email" miolo>
                <Card className="glass-strong border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Mail className="w-5 h-5 text-primary" />
                      {t('store.profile.change_email_title')}
                    </CardTitle>
                    <CardDescription>{t('store.profile.change_email_desc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleEmailChange} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('store.profile.current_email')}</Label>
                        <Input 
                          value={user?.email}
                          type="email"
                          className="h-11"
                          disabled
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newEmail">{t('store.profile.new_email')}</Label>
                        <Input 
                          id="newEmail"
                          value={emailData.newEmail}
                          onChange={(e) => setEmailData({ ...emailData, newEmail: e.target.value })}
                          type="email"
                          placeholder={t('store.profile.new_email_placeholder')}
                          className="h-11"
                          disabled={loadingEmail}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emailPassword">{t('store.profile.email_password')}</Label>
                        <Input 
                          id="emailPassword"
                          value={emailData.password}
                          onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                          type="password"
                          placeholder="••••••••"
                          className="h-11"
                          disabled={loadingEmail}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('store.profile.email_password_hint')}
                        </p>
                      </div>
                      <Button 
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11"
                        disabled={loadingEmail}
                      >
                        {loadingEmail ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('store.profile.changing')}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {t('store.profile.change_email_title')}
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                </Caixa>

                {/* Alterar Senha */}
                <Caixa id="perfil.configuracoes.senha" miolo>
                <Card className="glass-strong border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Shield className="w-5 h-5 text-primary" />
                      {t('store.profile.change_password_title')}
                    </CardTitle>
                    <CardDescription>{t('store.profile.change_password_desc')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">{t('store.profile.current_password')}</Label>
                        <Input 
                          id="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          type="password"
                          placeholder="••••••••"
                          className="h-11"
                          disabled={loadingPassword}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">{t('store.profile.new_password')}</Label>
                        <Input 
                          id="newPassword"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          type="password"
                          placeholder="••••••••"
                          className="h-11"
                          disabled={loadingPassword}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('store.profile.password_min')}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">{t('store.profile.confirm_password')}</Label>
                        <Input 
                          id="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          type="password"
                          placeholder="••••••••"
                          className="h-11"
                          disabled={loadingPassword}
                        />
                      </div>
                      <Button 
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11"
                        disabled={loadingPassword}
                      >
                        {loadingPassword ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('store.profile.changing')}
                          </>
                        ) : (
                          <>
                            <Shield className="w-4 h-4 mr-2" />
                            {t('store.profile.change_password_title')}
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                </Caixa>
              </div>
            </div>
          </TabsContent>

          {!lojaDigital && store?.id && (
            <TabsContent value="game" className="mt-0">
              <GameLinkCard storeId={store.id} />
            </TabsContent>
          )}

          {mostrarDiscord && discordLink && store?.id && (
            <TabsContent value="discord" className="mt-0">
              <DiscordLinkCard storeId={store.id} storeName={store.name} data={discordLink} onChange={setDiscordLink} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <AddCreditsDialog open={creditsDialogOpen} onOpenChange={setCreditsDialogOpen} />
      <OrderDetailModal
        orderId={selectedOrderId}
        open={orderDetailOpen}
        onOpenChange={(open) => {
          setOrderDetailOpen(open);
          if (!open) setSelectedOrderId(null);
        }}
      />
    </div>
  );
}
